import { api } from './client';
import type {
  BuyResult,
  DiscordBotSettings,
  DiscordRoleOption,
  InventoryGrant,
  Me,
  NotificationEmbedConfig,
  NotificationTemplate,
  Order,
  Product,
  PromoCheckResult,
  PromoCode,
  ShopCategory,
  TopupResponse,
  UserPass,
  UserRole,
} from './types';

type NotificationTemplateResponse = Omit<NotificationTemplate, 'embedConfig'> & {
  embedConfig: unknown;
};

function normalizeNotificationEmbedConfig(value: unknown): NotificationEmbedConfig | null {
  let parsed = value;

  // MariaDB/MySQL drivers can return JSON columns as serialized JSON. Handle both
  // the normal object response and the serialized form without breaking the editor.
  for (let attempt = 0; attempt < 2 && typeof parsed === 'string'; attempt += 1) {
    try {
      parsed = JSON.parse(parsed) as unknown;
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const input = parsed as Record<string, unknown>;
  const normalized: NotificationEmbedConfig = {};
  const color = input.color;
  if (color === null) {
    normalized.color = null;
  } else if (typeof color === 'number' && Number.isInteger(color) && color >= 0 && color <= 0xffffff) {
    normalized.color = color;
  }

  for (const key of [
    'bannerImageUrl',
    'imageUrl',
    'thumbnailUrl',
    'footerText',
    'footerIconUrl',
  ] as const) {
    const field = input[key];
    if (typeof field === 'string' || field === null) normalized[key] = field;
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
}

export const authApi = {
  me: () => api.get<Me>('/api/auth/me').then((r) => r.data),
  loginUrl: (returnTo?: string) => {
    const base = `${api.defaults.baseURL ?? ''}/api/auth/discord`;
    return returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base;
  },
  logout: () => api.post('/api/auth/logout').then((r) => r.data),
};

export type TeamMember = {
  id: number;
  name: string;
  role: string;
  icon: string;
  color: string;
  description: string | null;
  avatarUrl: string | null;
  sortOrder?: number;
  isVisible?: boolean;
};

export const teamApi = {
  list: () => api.get<TeamMember[]>('/api/team').then((r) => r.data),
};

export type FaqItem = { id: number; question: string; answer: string; sortOrder?: number; isVisible?: boolean };
export type RulesSection = { id: number; title: string; items: string[]; sortOrder?: number; isVisible?: boolean };
export type WikiArticle = {
  id: number;
  slug: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  content: string;
  sortOrder?: number;
  isVisible?: boolean;
};
export type WikiArticleCard = Pick<WikiArticle, 'id' | 'slug' | 'title' | 'summary' | 'imageUrl'>;

export type ApplicationStatus = 'pending' | 'accepted' | 'rejected';

export type ApplicationCapacity = {
  isEnabled: boolean;
  totalSlots: number;
  acceptedCount: number;
  availableSlots: number;
  maxVideoSizeMb: number;
  canSubmit: boolean;
};

export type MyApplication = {
  id: number;
  name: string | null;
  nickname: string;
  age: number;
  about: string;
  status: ApplicationStatus;
  videoOriginalName: string;
  videoSizeBytes: number;
  submittedAt: string;
  reviewedAt: string | null;
};

export type AdminApplication = MyApplication & {
  userId: number;
  videoMime: string;
  discordId: string | null;
  username: string | null;
  globalName: string | null;
  avatar: string | null;
};

export type AdminServerOption = {
  id: number;
  slug: string;
  name: string;
  isEnabled: boolean;
  sortOrder: number;
};

export type ApplicationStatusUpdateInput =
  | { status: Exclude<ApplicationStatus, 'accepted'> }
  | ({ status: 'accepted'; serverId: number | null } & (
      | {
          expiresAt: string | null;
          durationDays?: never;
          durationHours?: never;
        }
      | {
          expiresAt?: never;
          durationDays: number;
          durationHours: number;
        }
    ));

export type ApplicationStatusUpdateResult = {
  ok: true;
  status: ApplicationStatus;
  pass?: PassGrantResult;
};

export type ProductCategory = {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  isVisible: boolean;
};

export type Pass = {
  id: number;
  userId: number;
  discordId: string;
  username: string;
  nickname: string;
  serverId: number | null;
  serverName: string | null;
  source: 'order' | 'manual' | 'import';
  orderId: number | null;
  note: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
  createdAt: string;
};

export type PassGrantInput = {
  userId: number;
  serverId?: number | null;
  note?: string | null;
} & (
  | {
      expiresAt?: string | null;
      durationDays?: never;
      durationHours?: never;
    }
  | {
      expiresAt?: never;
      durationDays: number;
      durationHours: number;
    }
);

export type PassGrantResult = {
  id: number;
  operation: 'created' | 'extended';
  previousExpiresAt: string | null;
  reactivated: boolean;
  expiresAt: string | null;
  serverId: number | null;
};

export const faqApi = {
  list: () => api.get<FaqItem[]>('/api/faq').then((r) => r.data),
};
export const rulesApi = {
  list: () => api.get<RulesSection[]>('/api/rules').then((r) => r.data),
};
export const wikiApi = {
  list: () => api.get<WikiArticleCard[]>('/api/wiki').then((r) => r.data),
  get: (slug: string) => api.get<WikiArticle>(`/api/wiki/${slug}`).then((r) => r.data),
};

export const applicationsApi = {
  config: () => api.get<ApplicationCapacity>('/api/applications/config').then((r) => r.data),
  mine: () => api.get<MyApplication | null>('/api/applications/mine').then((r) => r.data),
  submit: (form: FormData, onProgress?: (percent: number) => void) =>
    api
      .post<{ id: number; status: 'pending' }>('/api/applications', form, {
        timeout: 0,
        onUploadProgress: (event) => {
          if (event.total && onProgress) {
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          }
        },
      })
      .then((r) => r.data),
};

export const shopApi = {
  products: (params?: { kind?: string; category?: string }) =>
    api
      .get<Product[]>('/api/shop/products', { params: params ?? {} })
      .then((r) => r.data),
  categories: () => api.get<ShopCategory[]>('/api/shop/categories').then((r) => r.data),
  buy: (args: { productId: number; durationIndex?: number; promoCode?: string }) =>
    api.post<BuyResult>('/api/shop/buy', args).then((r) => r.data),
  checkPromo: (args: { productId: number; durationIndex?: number; code: string }) =>
    api.post<PromoCheckResult>('/api/shop/check-promo', args).then((r) => r.data),
  myOrders: () => api.get<Order[]>('/api/shop/orders/mine').then((r) => r.data),
};

export const profileApi = {
  bindNickname: (nickname: string) =>
    api
      .post<{ ok: true; nickname: string }>('/api/profile/bind-nickname', { nickname })
      .then((r) => r.data),
  balance: () => api.get<{ balanceMinor: number }>('/api/profile/balance').then((r) => r.data),
  transactions: () => api.get('/api/profile/transactions').then((r) => r.data),
  passes: () => api.get<UserPass[]>('/api/profile/passes').then((r) => r.data),
  roles: () => api.get<UserRole[]>('/api/profile/roles').then((r) => r.data),
  inventory: () => api.get<InventoryGrant[]>('/api/profile/inventory').then((r) => r.data),
};

export const topupApi = {
  yookassa: (amountRub: number) =>
    api
      .post<TopupResponse>('/api/topup/yookassa', { amountRub })
      .then((r) => r.data),
  cryptobot: (amountRub: number) =>
    api.post<TopupResponse>('/api/topup/cryptobot', { amountRub }).then((r) => r.data),
  recheckPending: () =>
    api
      .post<{
        ok: true;
        checked: number;
        appliedCount: number;
        creditedMinor: number;
        providerErrors: number;
        removedExpiredCount: number;
      }>('/api/topup/recheck-pending')
      .then((r) => r.data),
};

export const adminApi = {
  stats: () => api.get('/api/admin/stats').then((r) => r.data),
  products: {
    list: () => api.get<Product[]>('/api/admin/products').then((r) => r.data),
    create: (p: Partial<Product> & { commands?: string[]; expireCommands?: string[] }) =>
      api.post('/api/admin/products', p).then((r) => r.data),
    update: (id: number, p: Partial<Product>) =>
      api.patch(`/api/admin/products/${id}`, p).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/products/${id}`).then((r) => r.data),
    toggle: (id: number) =>
      api
        .post<{ ok: true; isEnabled: boolean }>(`/api/admin/products/${id}/toggle`)
        .then((r) => r.data),
  },
  servers: {
    list: () => api.get<AdminServerOption[]>('/api/admin/servers').then((r) => r.data),
    create: (s: Record<string, unknown>) => api.post('/api/admin/servers', s).then((r) => r.data),
    update: (id: number, s: Record<string, unknown>) =>
      api.patch(`/api/admin/servers/${id}`, s).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/servers/${id}`).then((r) => r.data),
    toggle: (id: number) =>
      api
        .post<{ ok: true; isEnabled: boolean }>(`/api/admin/servers/${id}/toggle`)
        .then((r) => r.data),
  },
  users: {
    list: (q?: string, page = 1, perPage = 50) =>
      api.get('/api/admin/users', { params: { q, page, perPage } }).then((r) => r.data),
    update: (id: number, body: Record<string, unknown>) =>
      api.patch(`/api/admin/users/${id}`, body).then((r) => r.data),
    remove: (id: number) =>
      api.delete<{ ok: true }>(`/api/admin/users/${id}`).then((r) => r.data),
  },
  transactions: (page = 1, perPage = 50) =>
    api.get('/api/admin/transactions', { params: { page, perPage } }).then((r) => r.data),
  recheckTransaction: (id: number) =>
    api.post(`/api/admin/transactions/${id}/recheck`).then((r) => r.data),
  deleteTransaction: (id: number) =>
    api.delete(`/api/admin/transactions/${id}`).then((r) => r.data),
  deliveries: () => api.get('/api/admin/deliveries').then((r) => r.data),
  retryDelivery: (id: number) =>
    api.post(`/api/admin/deliveries/${id}/retry`).then((r) => r.data),
  team: {
    list: () => api.get<TeamMember[]>('/api/admin/team').then((r) => r.data),
    create: (m: Partial<TeamMember>) => api.post('/api/admin/team', m).then((r) => r.data),
    update: (id: number, m: Partial<TeamMember>) =>
      api.patch(`/api/admin/team/${id}`, m).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/team/${id}`).then((r) => r.data),
  },
  faq: {
    list: () => api.get<FaqItem[]>('/api/admin/faq').then((r) => r.data),
    create: (f: Partial<FaqItem>) => api.post('/api/admin/faq', f).then((r) => r.data),
    update: (id: number, f: Partial<FaqItem>) =>
      api.patch(`/api/admin/faq/${id}`, f).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/faq/${id}`).then((r) => r.data),
  },
  rules: {
    list: () => api.get<RulesSection[]>('/api/admin/rules').then((r) => r.data),
    create: (s: Partial<RulesSection>) => api.post('/api/admin/rules', s).then((r) => r.data),
    update: (id: number, s: Partial<RulesSection>) =>
      api.patch(`/api/admin/rules/${id}`, s).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/rules/${id}`).then((r) => r.data),
  },
  wiki: {
    list: () => api.get<WikiArticle[]>('/api/admin/wiki').then((r) => r.data),
    create: (article: Partial<WikiArticle>) =>
      api.post('/api/admin/wiki', article).then((r) => r.data),
    update: (id: number, article: Partial<WikiArticle>) =>
      api.patch(`/api/admin/wiki/${id}`, article).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/wiki/${id}`).then((r) => r.data),
  },
  applications: {
    settings: () =>
      api.get<ApplicationCapacity>('/api/admin/applications/settings').then((r) => r.data),
    updateSettings: (body: Pick<ApplicationCapacity, 'isEnabled' | 'totalSlots' | 'maxVideoSizeMb'>) =>
      api.patch<ApplicationCapacity>('/api/admin/applications/settings', body).then((r) => r.data),
    resetSlots: () =>
      api
        .post<ApplicationCapacity>('/api/admin/applications/settings/reset-slots')
        .then((r) => r.data),
    list: (params: { status?: ApplicationStatus; page?: number; perPage?: number } = {}) =>
      api
        .get<{ rows: AdminApplication[]; total: number; page: number; perPage: number }>(
          '/api/admin/applications',
          { params },
        )
        .then((r) => r.data),
    updateStatus: (id: number, body: ApplicationStatusUpdateInput) =>
      api
        .patch<ApplicationStatusUpdateResult>(`/api/admin/applications/${id}/status`, body)
        .then((r) => r.data),
    remove: (id: number) =>
      api.delete<{ ok: true }>(`/api/admin/applications/${id}`).then((r) => r.data),
    videoUrl: (id: number) => `${api.defaults.baseURL ?? ''}/api/admin/applications/${id}/video`,
  },
  categories: {
    list: () => api.get<ProductCategory[]>('/api/admin/categories').then((r) => r.data),
    allTab: () => api.get<ProductCategory>('/api/admin/categories/all-tab').then((r) => r.data),
    updateAllTab: (c: Pick<ProductCategory, 'isVisible'>) =>
      api.patch<{ ok: true; isVisible: boolean }>('/api/admin/categories/all-tab', c).then((r) => r.data),
    create: (c: Partial<ProductCategory>) =>
      api.post<{ id: number }>('/api/admin/categories', c).then((r) => r.data),
    update: (id: number, c: Partial<ProductCategory>) =>
      api.patch(`/api/admin/categories/${id}`, c).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/categories/${id}`).then((r) => r.data),
  },
  passes: {
    list: (
      params: {
        q?: string;
        userId?: number;
        status?: 'active' | 'revoked' | 'all';
        page?: number;
        perPage?: number;
      } = {},
    ) =>
      api
        .get<{ rows: Pass[]; total: number }>('/api/admin/passes', { params })
        .then((r) => r.data),
    grant: (body: PassGrantInput) =>
      api.post<PassGrantResult>('/api/admin/passes', body).then((r) => r.data),
    revoke: (id: number, reason?: string) =>
      api.post(`/api/admin/passes/${id}/revoke`, { reason }).then((r) => r.data),
  },
  templates: {
    list: () =>
      api
        .get<NotificationTemplateResponse[]>('/api/admin/notification-templates')
        .then((r) =>
          r.data.map((template) => ({
            ...template,
            embedConfig: normalizeNotificationEmbedConfig(template.embedConfig),
          })),
        ),
    update: (id: number, body: Partial<NotificationTemplate>) =>
      api.patch(`/api/admin/notification-templates/${id}`, body).then((r) => r.data),
  },
  discordSettings: {
    get: () => api.get<DiscordBotSettings>('/api/admin/discord-settings').then((r) => r.data),
    update: (body: DiscordBotSettings) =>
      api.patch<DiscordBotSettings>('/api/admin/discord-settings', body).then((r) => r.data),
    roles: () => api.get<DiscordRoleOption[]>('/api/admin/discord-roles').then((r) => r.data),
  },
  promos: {
    list: () => api.get<PromoCode[]>('/api/admin/promos').then((r) => r.data),
    create: (p: Partial<PromoCode>) => api.post<{ id: number }>('/api/admin/promos', p).then((r) => r.data),
    update: (id: number, p: Partial<PromoCode>) =>
      api.patch(`/api/admin/promos/${id}`, p).then((r) => r.data),
    remove: (id: number) => api.delete(`/api/admin/promos/${id}`).then((r) => r.data),
  },
};
